/**
 * Owns the bird's on-screen position, facing direction, and scale. Handles:
 *
 *  - Intro flight (1.6s easeOut smoothstep from offscreen edge to cursor)
 *  - Cursor-follow via a damped harmonic oscillator matching SwiftUI's
 *    `.spring(response: 0.2, dampingFraction: 0.6)` — used throughout
 *    `OverlayWindow.swift` for the native app
 *  - Idle perched detection (1.5s of no movement)
 *  - Bezier-arc flight to a target element for pointing
 *  - Horizontal flip based on direction of travel (4 px deadzone)
 *
 * Every number here comes from the native app's `OverlayWindow.swift`
 * so the feel matches. See `OverlayWindow.swift:160-185, 264, 567-704,
 * 913-999`.
 */

import type { BirdRenderer } from "./SpriteRenderer";
import { clampPerchTargetToChrome } from "./perchExclusion";

/** Rendered size of the bird in CSS pixels. Native uses 96pt. */
export const SPRITE_SIZE_PX = 96;

/** Offset of the bird from the cursor. */
const CURSOR_OFFSET_X = 55;
const CURSOR_OFFSET_Y = 38;

/** Cursor-follow timer interval — 60 fps. */
const FOLLOW_TICK_MS = 1000 / 60;

/** Seconds of cursor-idle before switching to perched. */
const CURSOR_IDLE_THRESHOLD_SECONDS = 1.5;

/** Duration of the intro fly-in. */
const INTRO_FLIGHT_DURATION_SECONDS = 1.6;

/** Deadzone before horizontal flip applies. */
const FLIP_DEADZONE_PX = 4;

/**
 * Maximum speed the bird is allowed to fly while spring-following the
 * cursor. The damped-oscillator math, left to its own devices, will
 * happily peg the bird's velocity at thousands of pixels per second on
 * a sudden cursor flick — which keeps it glued to the cursor and kills
 * any sense of inertia.
 *
 * Capping the speed makes the cursor "outrun" the bird on fast moves:
 * the bird falls behind, then chases at max-speed (showing the swoop
 * pose) until it catches up. Slow / casual cursor motion stays under
 * the cap, so the bird tracks normally with the spring smoothing.
 */
const MAX_FOLLOW_SPEED_PX_PER_SEC = 1100;

/**
 * Swoop is the visual companion to the catch-up phase. We enter swoop
 * when the bird is far from the cursor AND moving fast (i.e. it's in
 * pursuit). We exit when the bird has nearly arrived. Hysteresis on
 * the distance plus a minimum hold prevents flicker.
 */
const SWOOP_ENTER_DISTANCE_PX = 140;
const SWOOP_EXIT_DISTANCE_PX = 36;
const SWOOP_ENTER_SPEED_PX_PER_SEC = 700;
const SWOOP_MIN_HOLD_MS = 880;

/**
 * SwiftUI `.spring(response: 0.2, dampingFraction: 0.6)` translates to
 * the physics below. The oscillator is simulated per-axis in discrete
 * time steps so the bird accelerates toward the cursor, overshoots
 * slightly, and settles — not a linear catch-up.
 *
 *   ω₀  = 2π / response           (natural angular frequency)
 *   k   = ω₀²                      (stiffness, mass normalized to 1)
 *   c   = 2 · ζ · ω₀               (damping where ζ = dampingFraction)
 */
const SPRING_RESPONSE_SECONDS = 0.2;
const SPRING_DAMPING_FRACTION = 0.6;
const SPRING_NATURAL_FREQUENCY = (2 * Math.PI) / SPRING_RESPONSE_SECONDS;
const SPRING_STIFFNESS = SPRING_NATURAL_FREQUENCY * SPRING_NATURAL_FREQUENCY;
const SPRING_DAMPING = 2 * SPRING_DAMPING_FRACTION * SPRING_NATURAL_FREQUENCY;

export interface FlightTarget {
  /** Absolute viewport coordinates (CSS pixels) of the point to fly toward. */
  viewportX: number;
  viewportY: number;
}

type MovementMode =
  | "intro"
  | "follow"
  | "pointing-flight"
  | "pointing-hold"
  | "nest-flight"
  | "nested";

export class MovementController {
  private readonly containerElement: HTMLElement;
  private readonly spriteRenderer: BirdRenderer;
  private readonly onIdleStateChange: (isIdle: boolean) => void;

  private displayedX = 0;
  private displayedY = 0;
  private velocityX = 0;
  private velocityY = 0;
  private targetCursorX = 0;
  private targetCursorY = 0;
  private lastCursorMoveAt = 0;
  private isFacingLeft = true;
  private currentScale = 1;

  private mode: MovementMode = "intro";
  private followTimerId: number | null = null;
  private lastFollowTickAt = 0;
  private flightAnimationId: number | null = null;
  private idleBroadcastState: boolean | null = null;
  private pendingFlightResolve: (() => void) | null = null;
  private isSwooping = false;
  private swoopEnteredAt = 0;
  /**
   * KokoApp owns the sprite animation when voice state is non-idle
   * (listening / thinking / talking). The MovementController only
   * drives flight ↔ swoop ↔ perched transitions when this returns
   * true — i.e. while the bird is in passive cursor-follow mode.
   */
  private readonly canOwnAnimation: () => boolean;

  constructor(
    containerElement: HTMLElement,
    spriteRenderer: BirdRenderer,
    onIdleStateChange: (isIdle: boolean) => void,
    canOwnAnimation: () => boolean = () => true,
  ) {
    this.containerElement = containerElement;
    this.spriteRenderer = spriteRenderer;
    this.onIdleStateChange = onIdleStateChange;
    this.canOwnAnimation = canOwnAnimation;
    this.containerElement.style.position = "fixed";
    this.containerElement.style.left = "0";
    this.containerElement.style.top = "0";
    this.containerElement.style.width = `${SPRITE_SIZE_PX}px`;
    this.containerElement.style.height = `${SPRITE_SIZE_PX}px`;
    this.containerElement.style.pointerEvents = "auto";
    this.containerElement.style.willChange = "transform";
    this.containerElement.style.zIndex = "2147483646";
    this.applyTransform();
  }

  startTrackingCursor(): void {
    document.addEventListener("mousemove", this.handleMouseMove, { passive: true });
  }

  stopTrackingCursor(): void {
    document.removeEventListener("mousemove", this.handleMouseMove);
  }

  /**
   * Fly the bird from offscreen into its resting position at the cursor.
   * Resolves when the intro completes and spring-follow begins.
   */
  async playIntroFlight(): Promise<void> {
    this.mode = "intro";
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (this.lastCursorMoveAt === 0) {
      // No cursor movement yet — default to a natural resting spot in the
      // lower-right, but lifted well clear of the always-on status pill in
      // the bottom-right corner (otherwise the bird rests behind it and
      // looks missing on load).
      this.targetCursorX = viewportWidth - 200;
      this.targetCursorY = viewportHeight - 260;
      // Keep the default parking spot clear of fixed chrome — e.g. the
      // studio timeline dock restored expanded from a previous session
      // (yap 2026-07-07 perch discipline). Clamp the sprite CENTER the
      // resting position implies, then translate back to cursor coords.
      const restCenter = clampPerchTargetToChrome(
        {
          viewportX: this.targetCursorX + CURSOR_OFFSET_X + SPRITE_SIZE_PX / 2,
          viewportY: this.targetCursorY + CURSOR_OFFSET_Y + SPRITE_SIZE_PX / 2,
        },
        SPRITE_SIZE_PX,
      );
      this.targetCursorX = restCenter.viewportX - SPRITE_SIZE_PX / 2 - CURSOR_OFFSET_X;
      this.targetCursorY = restCenter.viewportY - SPRITE_SIZE_PX / 2 - CURSOR_OFFSET_Y;
    }

    const endX = this.targetCursorX + CURSOR_OFFSET_X;
    const endY = this.targetCursorY + CURSOR_OFFSET_Y;
    const startX = viewportWidth + SPRITE_SIZE_PX;
    const startY = Math.max(40, endY - 80);

    this.displayedX = startX;
    this.displayedY = startY;
    this.isFacingLeft = true;
    this.applyTransform();

    await new Promise<void>((resolve) => {
      const startedAt = performance.now();
      const step = (now: number) => {
        const elapsedSeconds = (now - startedAt) / 1000;
        const progress = Math.min(1, elapsedSeconds / INTRO_FLIGHT_DURATION_SECONDS);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        this.displayedX = startX + (endX - startX) * easedProgress;
        this.displayedY = startY + (endY - startY) * easedProgress;
        this.applyTransform();
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          // Seed the follow-spring with the intro's tail velocity so the
          // transition is seamless and the bird doesn't visually freeze.
          this.velocityX = (endX - startX) * 0.3;
          this.velocityY = (endY - startY) * 0.3;
          resolve();
        }
      };
      requestAnimationFrame(step);
    });

    this.mode = "follow";
    this.lastFollowTickAt = performance.now();
    this.beginFollowTimer();
  }

  async flyToPointingTarget(target: FlightTarget): Promise<void> {
    if (this.flightAnimationId !== null) {
      cancelAnimationFrame(this.flightAnimationId);
      this.flightAnimationId = null;
    }
    if (this.pendingFlightResolve) {
      this.pendingFlightResolve();
      this.pendingFlightResolve = null;
    }

    this.mode = "pointing-flight";
    this.stopFollowTimer();
    this.spriteRenderer.setAnimation("swoop");

    const startX = this.displayedX;
    const startY = this.displayedY;
    const endX = target.viewportX - SPRITE_SIZE_PX / 2;
    const endY = target.viewportY - SPRITE_SIZE_PX / 2;

    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    // Floor 0.85s + divisor 600 (was 0.6s / 800). Short hops were
    // bottoming out at the floor and snapping past so fast the user
    // couldn't register the pointing pose. The new pacing gives even
    // ~200px flights ~0.85s of visible arc while still letting long
    // cross-screen flights ride the 1.4s ceiling.
    const durationSeconds = Math.min(1.4, Math.max(0.85, distance / 600));
    const arcHeight = Math.min(distance * 0.2, 80);
    const controlX = (startX + endX) / 2;
    const controlY = (startY + endY) / 2 - arcHeight;

    this.isFacingLeft = endX < startX;

    await new Promise<void>((resolve) => {
      this.pendingFlightResolve = resolve;
      const startedAt = performance.now();
      const step = (now: number) => {
        const elapsed = (now - startedAt) / 1000;
        const progress = Math.min(1, elapsed / durationSeconds);
        const eased = progress * progress * (3 - 2 * progress);
        const oneMinusT = 1 - eased;
        this.displayedX =
          oneMinusT * oneMinusT * startX +
          2 * oneMinusT * eased * controlX +
          eased * eased * endX;
        this.displayedY =
          oneMinusT * oneMinusT * startY +
          2 * oneMinusT * eased * controlY +
          eased * eased * endY;
        this.currentScale = 1 + Math.sin(progress * Math.PI) * 0.3;
        this.applyTransform();
        if (progress < 1) {
          this.flightAnimationId = requestAnimationFrame(step);
        } else {
          this.flightAnimationId = null;
          this.currentScale = 1;
          this.applyTransform();
          this.pendingFlightResolve = null;
          resolve();
        }
      };
      this.flightAnimationId = requestAnimationFrame(step);
    });

    this.mode = "pointing-hold";
    this.spriteRenderer.setAnimation("pointing");
  }

  /**
   * While the bird is holding the pointing pose, snap its position to
   * a fresh target. Used by `FlightController` to keep the bird glued
   * to a DOM element through page scrolls or layout shifts. Direct
   * position assignment (no spring) — the element moved, the bird
   * moves with it instantly.
   */
  setPointingPosition(target: FlightTarget): void {
    if (this.mode !== "pointing-hold") return;
    const nextX = target.viewportX - SPRITE_SIZE_PX / 2;
    const nextY = target.viewportY - SPRITE_SIZE_PX / 2;
    // Flip the sprite if the bird "moved" horizontally enough that
    // it would face the wrong way after the snap. Without this, a
    // sideways scroll could leave the bird facing away from the
    // element it's pointing at.
    const horizontalDelta = nextX - this.displayedX;
    if (horizontalDelta < -FLIP_DEADZONE_PX) {
      this.isFacingLeft = true;
    } else if (horizontalDelta > FLIP_DEADZONE_PX) {
      this.isFacingLeft = false;
    }
    this.displayedX = nextX;
    this.displayedY = nextY;
    this.applyTransform();
  }

  returnToFollow(): void {
    this.mode = "follow";
    this.velocityX = 0;
    this.velocityY = 0;
    this.lastFollowTickAt = performance.now();
    this.spriteRenderer.setAnimation("flight");
    this.beginFollowTimer();
  }

  /**
   * Fly the bird on a bezier arc to a resting corner and transition to
   * the perched state. Called by `NestController` after the user has
   * been idle past the threshold.
   */
  async flyToNestTarget(target: FlightTarget): Promise<void> {
    if (this.flightAnimationId !== null) {
      cancelAnimationFrame(this.flightAnimationId);
      this.flightAnimationId = null;
    }
    if (this.pendingFlightResolve) {
      this.pendingFlightResolve();
      this.pendingFlightResolve = null;
    }

    this.mode = "nest-flight";
    this.stopFollowTimer();
    this.spriteRenderer.setAnimation("swoop");

    const startX = this.displayedX;
    const startY = this.displayedY;
    const endX = target.viewportX - SPRITE_SIZE_PX / 2;
    const endY = target.viewportY - SPRITE_SIZE_PX / 2;

    const dx = endX - startX;
    const dy = endY - startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const durationSeconds = Math.min(1.6, Math.max(0.7, distance / 700));
    const arcHeight = Math.min(distance * 0.2, 80);
    const controlX = (startX + endX) / 2;
    const controlY = (startY + endY) / 2 - arcHeight;

    // While in flight, the bird faces its direction of travel.
    this.isFacingLeft = endX < startX;

    await new Promise<void>((resolve) => {
      this.pendingFlightResolve = resolve;
      const startedAt = performance.now();
      const step = (now: number) => {
        const elapsed = (now - startedAt) / 1000;
        const progress = Math.min(1, elapsed / durationSeconds);
        const eased = progress * progress * (3 - 2 * progress);
        const oneMinusT = 1 - eased;
        this.displayedX =
          oneMinusT * oneMinusT * startX +
          2 * oneMinusT * eased * controlX +
          eased * eased * endX;
        this.displayedY =
          oneMinusT * oneMinusT * startY +
          2 * oneMinusT * eased * controlY +
          eased * eased * endY;
        this.applyTransform();
        if (progress < 1) {
          this.flightAnimationId = requestAnimationFrame(step);
        } else {
          this.flightAnimationId = null;
          this.pendingFlightResolve = null;
          resolve();
        }
      };
      this.flightAnimationId = requestAnimationFrame(step);
    });

    this.mode = "nested";
    this.velocityX = 0;
    this.velocityY = 0;
    // Once perched, face into the screen rather than out at the wall —
    // a bird in the right corner faces left, a bird in the left corner
    // faces right. Mirrors `OverlayWindow.swift:632-633`.
    const spriteCenterX = endX + SPRITE_SIZE_PX / 2;
    this.isFacingLeft = spriteCenterX > window.innerWidth / 2;
    this.spriteRenderer.setAnimation("perched");
    this.applyTransform();
  }

  /**
   * Wake the bird from a nested state. The cursor-follow spring takes
   * over immediately, yanking the bird back toward the cursor.
   */
  leaveNest(): void {
    if (this.mode !== "nested" && this.mode !== "nest-flight") return;
    if (this.flightAnimationId !== null) {
      cancelAnimationFrame(this.flightAnimationId);
      this.flightAnimationId = null;
    }
    this.mode = "follow";
    this.velocityX = 0;
    this.velocityY = 0;
    this.lastFollowTickAt = performance.now();
    this.spriteRenderer.setAnimation("flight");
    this.beginFollowTimer();
  }

  /**
   * Whether the bird is currently sitting at its nest target (mode
   * `"nested"` — not mid nest-flight, not out pointing). The
   * `NestController` re-clamp poll uses this to avoid re-flying the
   * bird while another flight owns its position.
   */
  isPerchedAtNest(): boolean {
    return this.mode === "nested";
  }

  getSpriteCenter(): { x: number; y: number } {
    return {
      x: this.displayedX + SPRITE_SIZE_PX / 2,
      y: this.displayedY + SPRITE_SIZE_PX / 2,
    };
  }

  isReady(): boolean {
    return this.mode !== "intro";
  }

  destroy(): void {
    this.stopTrackingCursor();
    this.stopFollowTimer();
    if (this.flightAnimationId !== null) {
      cancelAnimationFrame(this.flightAnimationId);
      this.flightAnimationId = null;
    }
  }

  private handleMouseMove = (event: MouseEvent): void => {
    this.targetCursorX = event.clientX;
    this.targetCursorY = event.clientY;
    this.lastCursorMoveAt = performance.now();
  };

  private beginFollowTimer(): void {
    if (this.followTimerId !== null) return;
    this.lastFollowTickAt = performance.now();
    this.followTimerId = window.setInterval(() => this.followTick(), FOLLOW_TICK_MS);
  }

  private stopFollowTimer(): void {
    if (this.followTimerId !== null) {
      window.clearInterval(this.followTimerId);
      this.followTimerId = null;
    }
  }

  /**
   * One tick of the damped harmonic oscillator. Real dt (not a fixed
   * 1/60) because setInterval drift would otherwise accumulate energy
   * in the spring. We also cap dt at 1/30 so a backgrounded tab
   * resuming doesn't launch the bird across the screen.
   */
  private followTick(): void {
    if (this.mode !== "follow") return;

    const now = performance.now();
    const rawDt = (now - this.lastFollowTickAt) / 1000;
    const dt = Math.min(rawDt, 1 / 30);
    this.lastFollowTickAt = now;

    const restX = this.targetCursorX + CURSOR_OFFSET_X;
    const restY = this.targetCursorY + CURSOR_OFFSET_Y;

    // Hooke's law per-axis: F = -k·(x - rest) - c·v
    const accelerationX =
      -SPRING_STIFFNESS * (this.displayedX - restX) - SPRING_DAMPING * this.velocityX;
    const accelerationY =
      -SPRING_STIFFNESS * (this.displayedY - restY) - SPRING_DAMPING * this.velocityY;

    this.velocityX += accelerationX * dt;
    this.velocityY += accelerationY * dt;

    // Clamp the bird's speed so a fast cursor flick visibly outruns it.
    // The spring still owns acceleration and deceleration; the cap just
    // limits how fast the bird can ever travel through the air. Without
    // this, the bird is glued to the cursor at any speed.
    const speedPxPerSec = Math.sqrt(
      this.velocityX * this.velocityX + this.velocityY * this.velocityY,
    );
    if (speedPxPerSec > MAX_FOLLOW_SPEED_PX_PER_SEC) {
      const speedScale = MAX_FOLLOW_SPEED_PX_PER_SEC / speedPxPerSec;
      this.velocityX *= speedScale;
      this.velocityY *= speedScale;
    }

    this.displayedX += this.velocityX * dt;
    this.displayedY += this.velocityY * dt;

    // Flip direction based on travel velocity past a small deadzone so the
    // bird doesn't jitter mid-settle.
    if (Math.abs(this.velocityX) > FLIP_DEADZONE_PX * 4) {
      this.isFacingLeft = this.velocityX < 0;
    }

    this.applyTransform();

    const idleSeconds =
      this.lastCursorMoveAt === 0
        ? Infinity
        : (now - this.lastCursorMoveAt) / 1000;
    const isIdle = idleSeconds > CURSOR_IDLE_THRESHOLD_SECONDS;
    if (isIdle !== this.idleBroadcastState) {
      this.idleBroadcastState = isIdle;
      this.onIdleStateChange(isIdle);
    }

    // Swoop ↔ flight ↔ perched only while the bird is in passive
    // cursor-follow. Voice state owns the sprite otherwise.
    //
    // Trigger logic: the bird tucks its wings into a swoop when it's
    // *behind* the cursor (distance > SWOOP_ENTER_DISTANCE_PX) AND
    // moving fast (it's actively chasing, not just lazily drifting).
    // It exits swoop only when it has nearly arrived
    // (distance < SWOOP_EXIT_DISTANCE_PX) and the minimum hold has
    // elapsed — the asymmetric distance thresholds are hysteresis so
    // the bird doesn't flicker at the edge.
    if (this.canOwnAnimation()) {
      const distanceToTargetPx = Math.sqrt(
        (restX - this.displayedX) * (restX - this.displayedX) +
          (restY - this.displayedY) * (restY - this.displayedY),
      );
      // `speedPxPerSec` was computed before the velocity cap; reuse it.
      const isFarBehind = distanceToTargetPx > SWOOP_ENTER_DISTANCE_PX;
      const isMovingFast = speedPxPerSec > SWOOP_ENTER_SPEED_PX_PER_SEC;

      if (this.isSwooping) {
        const heldLongEnough = now - this.swoopEnteredAt >= SWOOP_MIN_HOLD_MS;
        const arrivedAtTarget = distanceToTargetPx < SWOOP_EXIT_DISTANCE_PX;
        if (heldLongEnough && arrivedAtTarget) {
          this.isSwooping = false;
          this.spriteRenderer.setAnimation(isIdle ? "perched" : "flight");
        }
      } else if (isFarBehind && isMovingFast) {
        this.isSwooping = true;
        this.swoopEnteredAt = now;
        this.spriteRenderer.setAnimation("swoop");
      }
    }
  }

  private applyTransform(): void {
    const flipSign = this.isFacingLeft ? 1 : -1;
    this.containerElement.style.transform = `translate3d(${this.displayedX}px, ${this.displayedY}px, 0) scaleX(${flipSign}) scale(${this.currentScale})`;
  }
}
